import {
	ReactElement,
	useState,
	useEffect
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { DestinationWindow } from "client/Api/Apps/common";
import { newGuid, Guid } from "client_legacy/ndaw/guid";
import { ZermeloLiveInformationProvider, InterestedZermeloParty } from "./provider";
import { ZermeloScheduleResponseModel, ItemsResultStatus } from "client/Api/Workspace/LiveInformation/Zermelo/zermeloLiveInformation";
import {
	BusyIndicator,
	DateSlider,
	SliderWithToggle
} from "client/Components";
import { LiveTileContent } from "../LiveTileContent";
import { ZermeloListItem } from "./ZermeloListItem";
import { ScheduleKind } from "./misc";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import styles from "./ZermeloDaySchedule.module.less";
import { getStartOfDay } from "client/DateAndTime/dateTimeUtils";
import { TimeZone } from "client/DateAndTime/timezones";

const {
	Scrollable,
	List,
	NoResults
} = LiveTileContent;

const {
	Slider,
	Toggle
} = SliderWithToggle;

export type ZermeloDayScheduleProps = {
	destinationWindow: DestinationWindow;
	connectionId: number;
	provider: ZermeloLiveInformationProvider;
	date: Date;
	onDateChange(date: Date): void;
	isScheduleSelectionAvailable: boolean;
	onChangeScheduleKind(kind: ScheduleKind): void;
	isTodayButtonAvailable?: boolean;
	timezone: TimeZone;
	addAnalyticsEvent(): void;
};

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;

} & (
		{
			hasTooManyConsecutiveErrors: true;
			hasGlobalFailure?: undefined;
		}
		|
		{
			hasGlobalFailure: true;
			hasTooManyConsecutiveErrors?: undefined;
		}
		|
		{
			hasTooManyConsecutiveErrors: false;
			hasGlobalFailure: false;
			isTeacher: boolean;
			response: ZermeloScheduleResponseModel;
		}
	);

export const ZermeloDaySchedule = (props: ZermeloDayScheduleProps): ReactElement => {
	const {
		connectionId,
		destinationWindow,
		provider,
		date,
		onDateChange,
		isScheduleSelectionAvailable,
		onChangeScheduleKind,
		isTodayButtonAvailable,
		timezone,
		addAnalyticsEvent
	} = props;

	const [partyId] = useState<Guid>(() => newGuid());
	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	const day: Date = getStartOfDay(date, timezone);
	const comparableDay: number = day.getTime();

	useEffect(
		() => {
			const party: InterestedZermeloParty = {
				partyId,
				resourceId: {
					connectionId,
					date: new Date(comparableDay)
				},
				informAboutGlobalRetrievalFailure: (): void => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: (): void => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				updateDaySchedule: (response: ZermeloScheduleResponseModel, isTeacher: boolean): void => {
					setLoadingState((x) => ({
						...x,
						isLoading: false,
						hasTooManyConsecutiveErrors: false,
						hasGlobalFailure: false,
						isTeacher,
						response
					}));
				}
			};

			setLoadingState((x) => ({
				...x,
				isLoading: true
			}));

			provider.register(party, { daySchedule: true});

			return () => {
				provider.unregister(party);
			};
		},
		[
			connectionId,
			partyId,
			provider,
			comparableDay
		]
	);

	const tileRefreshHandler = (): void => {
		setLoadingState((x) => ({
			...x,
			isLoading: true
		}));
		provider.forceReload();
	};

	const renderContent = (): ReactElement => {
		if (loadingState.isLoading) {
			return <BusyIndicator />;
		}

		if (loadingState.hasTooManyConsecutiveErrors) {
			return <TileRefresh onRefresh={tileRefreshHandler} />;
		}

		if (loadingState.hasGlobalFailure || loadingState.response.status !== ItemsResultStatus.Success) {
			return (
				<NoResults
					trackingId="has-error"
					icon="gear"
					title={localize("_UnableToRetrieveItems")}
					message={localize("_WeWereUnableToRetrieveTheItems")}
					onRetry={tileRefreshHandler}
				/>
			);
		}

		const {
			response,
			isTeacher
		} = loadingState;

		if (response.items.length === 0) {
			return (
				<NoResults
					trackingId="no-classes"
					icon="calendar"
					title={localize("_NoClassesThisDay")}
					message={localize("_YouDontHaveAnyClassesThisDay")}
				/>
			);
		}

		return (
			<Scrollable>
				<List>
					{ response.items.map((x) => (
						<ZermeloListItem
							key={x.id ?? x.startDateTime}
							destinationWindow={destinationWindow}
							isTeacher={isTeacher}
							appointment={x}
							timezone={timezone}
							onClick={addAnalyticsEvent}
						/>
					))}
				</List>
			</Scrollable>
		);
	};

	return (
		<>
			<div className={styles.sliderContainer}>
				<SliderWithToggle>
					<Slider>
						<DateSlider
							value={day}
							onChange={onDateChange}
							isTodayButtonAvailable={isTodayButtonAvailable}
							timezone={timezone}
						/>
					</Slider>
					{isScheduleSelectionAvailable && (
						<Toggle
							icon="calendar-week"
							onClick={() => onChangeScheduleKind(ScheduleKind.Week)}
						/>
					)}
				</SliderWithToggle>
			</div>
			{ renderContent() }
		</>
	);
};
ZermeloDaySchedule.displayName = "ZermeloDaySchedule";